import React, { useEffect, useRef } from "react";
import { TID } from "@/constants/tids";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, Trash2 } from "lucide-react";

// Hoisted so the array reference is stable across renders (avoids re-renders).
const REMARK_PLUGINS = [remarkGfm];

function MessageBubble({ msg, onToggleInclude, onRemove }) {
  return (
    <div className="space-y-2">
      {/* Question */}
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-sm px-4 py-2 max-w-[85%] text-sm">
          {msg.question}
        </div>
      </div>
      {/* Answer */}
      <div className="flex justify-start">
        <Card className="max-w-[92%] w-full">
          <CardContent className="pt-4">
            {msg.answer === null ? (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing…
              </p>
            ) : (
              <>
                <article className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-strong:text-foreground">
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{msg.answer}</ReactMarkdown>
                </article>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                    <Switch
                      data-testid={TID.chatIncludeToggle}
                      checked={!!msg.included}
                      onCheckedChange={() => onToggleInclude(msg)}
                    />
                    Include in final report
                  </label>
                  <button
                    onClick={() => onRemove(msg)}
                    className="text-muted-foreground hover:text-primary p-1"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function MessageList({ messages, loading, onToggleInclude, onRemove }) {
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  return (
    <div className="space-y-4" data-testid={TID.chatMessages}>
      {messages.map((msg) => (
        <MessageBubble key={msg.id} msg={msg} onToggleInclude={onToggleInclude} onRemove={onRemove} />
      ))}
      <div ref={endRef} />
    </div>
  );
}
